import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  LinearProgress,
  Typography,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Tooltip,
} from "@mui/material";
import { parseVtt } from "./utils";
import { TranscriptOverlay } from "./TranscriptOverlay";
import { TranscriptPanel } from "./TranscriptPanel";
import { Controls } from "./Controls";
import {
  Search,
  Close,
  ArrowUpwardRounded,
} from "@mui/icons-material";
import { useZoomPlayerStyles } from "./style";
import { isMobile } from "react-device-detect";
import { saveAs } from "file-saver";
import { LoadingSpinner } from "../loading-spinners";
import { DownloadSimple } from "@phosphor-icons/react";
import { PrimaryButton } from "../form";
import FlexBox from "../layouts/Flexbox";

export type ZoomPlayerProps = {
  videoUrl: string;
  vttUrl?: string;
  downloadUrls?: Record<string, string>;
  title?: string;
  summaryData?: Record<string, string[]> | null;
};

const ZoomPlayer: React.FC<ZoomPlayerProps> = ({
  videoUrl,
  vttUrl,
  downloadUrls,
  title,
  summaryData,
}) => {
  const classes = useZoomPlayerStyles();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const transcriptRef = useRef<HTMLDivElement | null>(null);

  const [currentTime, setCurrentTime] = useState(0);
  const [transcript, setTranscript] = useState<
    Array<{ start: number; end: number; text: string; speaker: string }>
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [seekingToTime, setSeekingToTime] = useState<number | null>(null);
  const [showTranscript, setShowTranscript] = useState(true);
  const [showTranscriptOverlay, setShowTranscriptOverlay] = useState(false);
  const [isTranscriptAvailable, setIsTranscriptAvailable] = useState(true);
  const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);
  const [isVideoError, setIsVideoError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  const filteredTranscript = searchTerm
    ? transcript.filter((line) =>
      line.text.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : transcript;

  const activeLineIndex = transcript.findIndex(
    (line) => currentTime >= line.start && currentTime <= line.end
  );

  const currentTranscriptLine = transcript.find((line) => {
    const epsilon = 0.2;
    return (
      currentTime + epsilon >= line.start && currentTime < line.end + epsilon
    );
  });

  const getExtensionFromResponse = (
    response: Response,
    fallbackUrl: string
  ) => {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("mp4")) return ".mp4";
    if (contentType.includes("vtt")) return ".vtt";
    if (contentType.includes("text/plain")) return ".txt";
    if (contentType.includes("json")) return ".json";
    const urlPath = fallbackUrl.split("?")[0];
    const last = urlPath.split("/").pop() || "";
    const dot = last.lastIndexOf(".");
    return dot > -1 ? last.slice(dot) : "";
  };

  const handleDownloadVideo = async () => {
    try {
      setIsDownloading(true);
      if (!videoUrl) return;

      const res = await fetch(videoUrl, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to download video");
      const blob = await res.blob();
      const ext = getExtensionFromResponse(res, videoUrl);
      saveAs(blob, `recording_video${ext}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadTranscript = async () => {
    try {
      setIsDownloading(true);
      const transcriptUrl = vttUrl;
      if (!transcriptUrl) return;

      const res = await fetch(transcriptUrl, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to download transcript");
      const blob = await res.blob();
      const ext = getExtensionFromResponse(res, transcriptUrl);
      saveAs(blob, `recording_transcript${ext}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleTranscriptClick = useCallback((line: { start: number }) => {
    if (videoRef.current) {
      setSeekingToTime(line.start);
      videoRef.current.currentTime = line.start;
      setCurrentTime(line.start);
    }
  }, []);

  useEffect(() => {
    if (vttUrl) {
      setIsTranscriptLoading(true);
      setIsTranscriptAvailable(false);
      fetch(vttUrl)
        .then((res) => {
          if (!res.ok) throw new Error("Transcript not available");
          return res.text();
        })
        .then((text) => {
          const cues = parseVtt(text);
          setTranscript(cues);
          setIsTranscriptAvailable(!!cues?.length);
        })
        .catch(() => {
          setTranscript([]);
          setIsTranscriptAvailable(false);
        })
        .finally(() => {
          setIsTranscriptLoading(false);
        });
    } else {
      setIsTranscriptLoading(false);
      setIsTranscriptAvailable(false);
      setTranscript([]);
    }
  }, [vttUrl]);

  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const suppressScrollRef = useRef(false);
  const suppressScrollUntilRef = useRef<number>(0);

  useEffect(() => {
    if (activeLineIndex >= 0 && transcriptRef.current) {
      if (isAutoScrollPaused) return;
      const activeElement = transcriptRef.current.children[activeLineIndex] as
        | HTMLElement
        | undefined;
      if (activeElement) {
        suppressScrollRef.current = true;
        suppressScrollUntilRef.current = Date.now() + 1000; 

        
        const container = transcriptRef.current;
        const elementRect = activeElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementTop =
          elementRect.top - containerRect.top + container.scrollTop;
        const elementCenter =
          elementTop - containerRect.height / 2 + elementRect.height / 2;

        container.scrollTo({
          top: elementCenter,
          behavior: "smooth",
        });

        setTimeout(() => {
          suppressScrollRef.current = false;
        }, 1000);
      }
    }
  }, [activeLineIndex, isAutoScrollPaused]);

  const handleUserTranscriptInteract = () => {
    if (suppressScrollRef.current) return;
    if (Date.now() < suppressScrollUntilRef.current) return;
    setIsAutoScrollPaused(true);
  };

  const resumeAutoScroll = () => {
    setIsAutoScrollPaused(false);
    if (activeLineIndex >= 0 && transcriptRef.current) {
      const activeElement = transcriptRef.current.children[activeLineIndex] as
        | HTMLElement
        | undefined;
      if (activeElement) {
        suppressScrollRef.current = true;
        suppressScrollUntilRef.current = Date.now() + 1000;

        
        const container = transcriptRef.current;
        const elementRect = activeElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementTop =
          elementRect.top - containerRect.top + container.scrollTop;
        const elementCenter =
          elementTop - containerRect.height / 2 + elementRect.height / 2;

        container.scrollTo({
          top: elementCenter,
          behavior: "smooth",
        });

        setTimeout(() => {
          suppressScrollRef.current = false;
        }, 1000);
      }
    }
  };

  const hasSummary = !!summaryData && Object.keys(summaryData || {}).length > 0;

  return (
    <Box className={classes.container} gap={2}>
      <Box
        className={`${!showTranscript ? classes.leftPaneFull : classes.leftPane
          }`}
      >
        {title && (
          <Box className={classes.playerHeader} mb={1}>
            <Typography className={classes.playerTitle}>{title}</Typography>
          </Box>
        )}
        <Box
          className={`${classes.videoContainer} ${!showTranscript ? classes.videoContainerFull : ""
            }`}
          sx={{ borderRadius: 2 }}
        >
          {isLoading && (
            <Box className={classes.loadingOverlay}>
              <LinearProgress className={classes.progressBar} />
            </Box>
          )}

          {isVideoError && (
            <Box className={classes.errorOverlay}>
              <Typography variant="body1" className={classes.textWhite}>
                Failed to load the recording.
              </Typography>
              <Typography variant="body2" className={classes.textMuted}>
                Please check your connection or try again.
              </Typography>
              <Chip
                label="Retry"
                onClick={() => {
                  setIsVideoError(false);
                  setIsLoading(true);
                  videoRef.current?.load();
                }}
                className={classes.retryChip}
                variant="outlined"
              />
            </Box>
          )}

          {isBuffering && !isLoading && !isVideoError && (
            <Box className={classes.bufferingOverlay}>
              <Box className={classes.spinner} />
              <Typography variant="body2" className={classes.textWhite}>
                Seeking...
              </Typography>
            </Box>
          )}

          <video
            ref={videoRef}
            src={videoUrl}
            controls
            className={classes.videoEl}
            controlsList="nodownload"
            disablePictureInPicture
            playsInline
            onPlay={() => setHasStartedPlaying(true)}
            onTimeUpdate={(e) => {
              const t = e.currentTarget.currentTime;
              if (!hasStartedPlaying && t > 0) setHasStartedPlaying(true);
              setCurrentTime(t);
            }}
            onLoadedMetadata={() => setIsLoading(false)}
            onSeeking={() => setIsBuffering(true)}
            onSeeked={() => {
              setIsBuffering(false);
              setSeekingToTime(null);
            }}
            onWaiting={() => setIsBuffering(true)}
            onCanPlay={() => setIsBuffering(false)}
            onError={() => {
              setIsVideoError(true);
              setIsLoading(false);
              setIsBuffering(false);
            }}
          />

          {showTranscriptOverlay && currentTranscriptLine && (
            <TranscriptOverlay line={currentTranscriptLine} />
          )}

          <div className={classes.controlsBar}>
            <Controls
              showTranscript={showTranscript}
              onToggleTranscript={() => setShowTranscript(!showTranscript)}
              showTranscriptOverlay={showTranscriptOverlay}
              onToggleTranscriptOverlay={() =>
                setShowTranscriptOverlay(!showTranscriptOverlay)
              }
              isTranscriptAvailable={isTranscriptAvailable}
            />
          </div>
        </Box>
        {hasSummary && !isMobile && (
          <Box className={classes.summaryWrapper}>
            <Typography className={classes.summaryTitle}>Summary</Typography>
            <Box>
              {Object.keys(summaryData || {}).map((key) => (
                <Box key={key} mb={1.5}>
                  <Typography className={classes.summarySectionTitle}>
                    {key}
                  </Typography>
                  <Box component="ul" className={classes.summaryList}>
                    {(summaryData?.[key] || []).map((item, idx) => (
                      <Box
                        key={idx}
                        component="li"
                        className={classes.summaryItem}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {showTranscript && (
        <Box className={classes.transcriptPanel}>
          <Box className={classes.transcriptHeader}>
            <FlexBox justifyContent="space-between" alignItems="center" gap={1} mb={1}>
              <Typography variant="body-mono-medium">Transcript</Typography>
              <FlexBox gap={1}>
                <Tooltip
                  title={
                    isDownloading
                      ? "Preparing download..."
                      : "Download video file"
                  }
                >
                  <span>
                    <PrimaryButton
                      onClick={handleDownloadVideo}
                      size="small"
                      startIcon={<DownloadSimple size={20} />}
                      disabled={
                        isDownloading ||
                        !downloadUrls ||
                        (!videoUrl)
                      }
                    >
                      Video
                    </PrimaryButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    isDownloading
                      ? "Preparing download..."
                      : "Download transcript file"
                  }
                >
                  <span>
                    <PrimaryButton
                      size="small"
                      startIcon={<DownloadSimple size={20} />}
                      onClick={handleDownloadTranscript}
                      disabled={!isTranscriptAvailable}
                    >
                      Transcript
                    </PrimaryButton>
                  </span>
                </Tooltip>
              </FlexBox>
            </FlexBox>
            <TextField
              size="small"
              placeholder={isTranscriptLoading ? "Loading transcript..." : "Search transcript..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={classes.searchField}
              disabled={!isTranscriptAvailable || isTranscriptLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchTerm("")}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              fullWidth
            />
          </Box>

          {isTranscriptLoading ? (
            <Box className={classes.transcriptScroll} sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              gap: 2
            }}>
              <LoadingSpinner size={40} loading={true} />
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Loading transcript...
              </Typography>
            </Box>
          ) : isTranscriptAvailable ? (
            <>
              <TranscriptPanel
                transcript={filteredTranscript}
                currentTime={currentTime}
                seekingToTime={seekingToTime}
                onSeek={handleTranscriptClick}
                transcriptRef={transcriptRef}
                onUserInteract={handleUserTranscriptInteract}
              />
              {isAutoScrollPaused && hasStartedPlaying && (
                <Box className={classes.resumeBar}>
                  <Button
                    size="small"
                    className={classes.resumeBtn}
                    onClick={resumeAutoScroll}
                    startIcon={<ArrowUpwardRounded fontSize="small" />}
                  >
                    Follow transcript
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box className={classes.transcriptScroll} sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              gap: 2
            }}>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Transcript not available for this recording.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {hasSummary && isMobile && (
        <Box className={classes.summaryWrapper}>
          <Typography className={classes.summaryTitle}>Summary</Typography>
          <Box>
            {Object.keys(summaryData || {}).map((key) => (
              <Box key={key} mb={1.5}>
                <Typography className={classes.summarySectionTitle}>
                  {key}
                </Typography>
                <Box component="ul" className={classes.summaryList}>
                  {(summaryData?.[key] || []).map((item, idx) => (
                    <Box
                      key={idx}
                      component="li"
                      className={classes.summaryItem}
                    >
                      {item}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ZoomPlayer;
